import React, { Component, ErrorInfo, ReactNode } from 'react';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import HomeIcon from '@mui/icons-material/Home';
import RefreshIcon from '@mui/icons-material/Refresh';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Button,
  Paper,
  Stack,
  Typography,
} from '@mui/material';
import { t } from 'ttag';

interface ErrorBoundaryProps {
  children: ReactNode;
  fallback?: (props: {
    error: Error | null;
    resetErrorBoundary: () => void;
    redirectToHome: () => void;
  }) => ReactNode;
  onReset?: () => void;
  homeRoute?: string;
}

interface ErrorBoundaryState {
  hasError: boolean;
  error: Error | null;
  errorInfo: ErrorInfo | null;
}

export class ErrorBoundary extends Component<
  ErrorBoundaryProps,
  ErrorBoundaryState
> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null,
    };
  }

  static getDerivedStateFromError(): Partial<ErrorBoundaryState> {
    return { hasError: true };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo): void {
    console.error('Error caught by ErrorBoundary:', error, errorInfo);

    this.setState({
      error: error,
      errorInfo: errorInfo,
    });
  }

  resetErrorBoundary = (): void => {
    this.setState({
      hasError: false,
      error: null,
      errorInfo: null,
    });

    if (this.props.onReset) {
      this.props.onReset();
    }

    // Reload the page
    window.location.reload();
  };

  redirectToHome = (): void => {
    this.setState({
      hasError: false,
      error: null,
      errorInfo: null,
    });

    window.location.href = this.props.homeRoute || '/';
  };

  render(): ReactNode {
    if (this.state.hasError) {
      return this.props.fallback ? (
        this.props.fallback({
          error: this.state.error,
          resetErrorBoundary: this.resetErrorBoundary,
          redirectToHome: this.redirectToHome,
        })
      ) : (
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            minHeight: '100vh',
            bgcolor: 'background.default',
            p: 2,
          }}
        >
          <Paper
            elevation={3}
            sx={{
              maxWidth: 600,
              width: '100%',
              mt: 4,
              p: 4,
              textAlign: 'center',
            }}
          >
            <Typography variant="h1" gutterBottom>
              {t`Something went wrong`}
            </Typography>

            <Typography variant="h4" color="text.secondary" sx={{ mb: 3 }}>
              {t`We apologize for the inconvenience. Please try resetting the application or return to the home page.`}
            </Typography>

            <Accordion sx={{ mb: 3 }}>
              <AccordionSummary
                expandIcon={<ExpandMoreIcon />}
                aria-controls="error-details-content"
                id="error-details-header"
              >
                <Typography variant="h3">{t`Error Details`}</Typography>
              </AccordionSummary>
              <AccordionDetails sx={{ textAlign: 'left' }}>
                <Typography
                  component="pre"
                  sx={{ whiteSpace: 'pre-wrap', fontSize: 12 }}
                >
                  {this.state.error && this.state.error.toString()}
                  {this.state.errorInfo &&
                    `\n\nComponent Stack:\n${this.state.errorInfo.componentStack}`}
                </Typography>
              </AccordionDetails>
            </Accordion>

            <Stack direction="row" spacing={2} justifyContent="center">
              <Button
                variant="contained"
                color="primary"
                startIcon={<RefreshIcon />}
                onClick={this.resetErrorBoundary}
              >
                {t`Try Again`}
              </Button>
              <Button
                variant="outlined"
                color="secondary"
                startIcon={<HomeIcon />}
                onClick={this.redirectToHome}
              >
                {t`Back to Home`}
              </Button>
            </Stack>
          </Paper>
        </Box>
      );
    }

    return this.props.children;
  }
}
